import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth, API } from "@/App";
import axios from "axios";
import { SOSBanner } from "@/components/custom/SOSBanner";
import {
  PawPrint, ArrowRight, Heart, ShieldAlert, Users, MapPin,
  Clock, TrendingUp, UserPlus, Search,
  ArrowUpRight,
  CloudRain, Flame, Snowflake, Wind, AlertTriangle, Zap,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EBtn, stripEmojis } from "@/styles/editorial";

const EMTYPE_ICONS = {
  dana: CloudRain, inundacion: CloudRain, incendio: Flame,
  terremoto: AlertTriangle, huracan: Wind, sequia: Zap,
  nevada: Snowflake, ataque: AlertTriangle,
  abandono_masivo: PawPrint, epidemia: ShieldAlert, otro: AlertTriangle,
};
const EMTYPE_LABELS = {
  dana: "DANA", inundacion: "Inundación", incendio: "Incendio",
  terremoto: "Terremoto", huracan: "Huracán", sequia: "Sequía",
  nevada: "Nevada", ataque: "Ataque", abandono_masivo: "Abandono masivo",
  epidemia: "Epidemia", otro: "Otro",
};

const statusColor = (status) =>
  status === "resuelto" || status === "resuelta" ? "#81801a"
  : (status || "").includes("coordina") ? "#fa9000"
  : "#f64e0e";

const NAV_LINKS = [
  { l: "Hub", h: "/", r: true },
  { l: "Recursos", h: "/recursos/ciudadania/guias", r: true },
  { l: "Prepara", h: "/prepara", r: true },
  { l: "Aprende", h: "/aprende", r: true },
  { l: "Fauna", h: "/fauna", r: true },
  { l: "Blog", h: "/blog", r: true },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [emergencies, setEmergencies] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats/public`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/emergencies?status=activo`).then((r) => setEmergencies(r.data.slice(0, 2))).catch(() => {});
  }, []);

  const goPanel = () => navigate(user ? (user.role === "protectora" ? "/protectora" : "/dashboard") : "/auth");

  return (
    <div className="min-h-screen" data-testid="landing-page" style={{ fontFamily: BODY }}>

      <style>{`
        @keyframes ea-step-slide {
          0% { transform: translateX(0); }
          45% { transform: translateX(28px); }
          100% { transform: translateX(0); }
        }
        .ea-step-row:hover { animation: ea-step-slide 0.9s cubic-bezier(0.33, 1, 0.68, 1); }
      `}</style>

      {/* SEO */}
      <Helmet>
        <title>Conecta: Voluntariado y Ayuda en Emergencias | Emergencia Animal</title>
        <meta name="description" content="Plataforma solidaria que conecta voluntarios con protectoras y refugios de animales durante emergencias. Ofrece tu ayuda o publica necesidades urgentes." />
        <link rel="canonical" href="https://emergenciaanimal.org/conecta" />
        <meta property="og:title" content="Conecta — Emergencia Animal" />
        <meta property="og:description" content="Red de voluntariado para proteger animales en emergencias." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://emergenciaanimal.org/conecta" />
      </Helmet>

      {/* Schema.org Structured Data */}
      {emergencies.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(
          emergencies.map((emg) => ({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": stripEmojis(emg.title),
            "description": stripEmojis(emg.description),
            "startDate": emg.created_at,
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "location": {
              "@type": "Place",
              "name": emg.location_name || "Spain",
              "address": { "@type": "PostalAddress", "addressRegion": emg.location_name || "Spain", "addressCountry": emg.country || "ES" },
            },
            "organizer": { "@type": "Organization", "name": "Emergencia Animal", "url": "https://emergenciaanimal.org" },
          }))
        ) }} />
      )}

      {/* NAV */}
      <header className="sticky top-0 z-50 px-6 md:px-12 py-3" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="flex items-center gap-2"
              style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="conecta-home-logo">
              <img src="/logos/logo-conecta.png" alt="Conecta" className="w-7 h-7 object-contain" />
              <span className="text-sm font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: C.white }}>Conecta</span>
            </button>
            <div className="h-4 w-px hidden md:block" style={{ background: "rgba(255,255,255,0.15)" }} />
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((lnk) => (
                <button key={lnk.l} onClick={() => navigate(lnk.h)}
                  className="px-3 py-1.5 text-[11px] font-semibold tracking-wider uppercase transition-all"
                  style={{ fontFamily: MONO, color: "rgba(255,255,255,0.55)", background: "none", border: "none", cursor: "pointer" }}
                  data-testid={`conecta-nav-${lnk.l.toLowerCase()}`}
                >{lnk.l}</button>
              ))}
            </nav>
          </div>
          <EBtn onClick={goPanel} bg={C.orange} color={C.navy} testId="conecta-cta-nav">
            {user ? "Mi panel" : "Acceder"} <ArrowUpRight className="w-3.5 h-3.5" />
          </EBtn>
        </div>
      </header>

      <SOSBanner />

      {/* ═══ HERO (H1) ═══ */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="relative w-full" style={{ height: "clamp(360px, 55vw, 580px)" }}>
          <img
            src="https://customer-assets.emergentagent.com/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/u1ws8l3w_f5402220-76f9-4ee9-a330-73b721b2b90f.jpg"
            alt="Bombero rescatando un perro en una emergencia"
            className="w-full h-full object-cover"
            data-testid="hero-rescue-image"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(41,41,77,0.95) 0%, rgba(41,41,77,0.4) 50%, transparent 100%)" }} />
          <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-12 md:pb-16">
            <div className="max-w-[1400px] mx-auto">
              <Reveal>
                <p className="text-[10px] font-semibold tracking-[0.25em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>
                  Red de emergencias para animales
                </p>
                <h1 className="text-[clamp(2.5rem,6vw,5rem)] leading-[0.9] tracking-tight mb-6" style={{ fontFamily: DISPLAY, color: C.white }}>
                  Conecta.<br /><span style={{ color: C.orange }}>Ayuda.</span><br />Salva.
                </h1>
                <p className="text-sm md:text-base mb-8 leading-relaxed" style={{ color: "rgba(255,255,255,0.65)", maxWidth: "65ch" }}>
                  Cuando llega una emergencia, cada minuto cuenta. Conectamos voluntarios
                  con protectoras que necesitan ayuda urgente.
                </p>
                <div className="flex flex-wrap gap-3">
                  <EBtn onClick={goPanel} bg={C.orange} color={C.navy} testId="hero-cta-btn-2">
                    {user ? "Ir a mi panel" : "Quiero ayudar"} <ArrowRight className="w-4 h-4" />
                  </EBtn>
                  <EBtn onClick={() => navigate("/onboarding")} outline bg="rgba(255,255,255,0.6)" testId="hero-onboarding-btn">
                    Cómo funciona
                  </EBtn>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
        {stats && (
          <div className="px-6 md:px-12 py-5" style={{ background: C.navy, borderTop: `3px solid ${C.blue}` }}>
            <div className="max-w-[1400px] mx-auto flex flex-wrap items-center gap-x-10 gap-y-3">
              {[
                { label: "Emergencias activas", value: stats.active_emergencies, c: C.orangeRed },
                { label: "Necesidades abiertas", value: stats.open_needs, c: C.pink },
                { label: "Voluntarios", value: stats.volunteers, c: C.blue },
                { label: "Protectoras", value: stats.shelters, c: C.lilac },
              ].map((s, i) => (
                <div key={i} className="flex items-baseline gap-2" data-testid={`stat-card-${i}`}>
                  <span className="text-xl font-bold" style={{ fontFamily: DISPLAY, color: s.c }}>{s.value}</span>
                  <span className="text-[9px] tracking-wider font-semibold uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.4)" }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Zona neutra entre bloques saturados (azul noche / naranja alerta) */}
      <div aria-hidden="true" style={{ background: C.offwhite, height: "var(--space-neutral)" }} data-testid="neutral-zone-1" />

      {/* ═══ ALERT — full-width orange-red block ═══ */}
      <section style={{ background: C.orangeRed }} className="cursor-pointer" onClick={() => navigate("/needs")} data-testid="conecta-venezuela-alert">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center gap-4">
          <ShieldAlert className="w-6 h-6 flex-shrink-0 text-white" strokeWidth={2.5} />
          <div className="flex-1">
            <p className="font-bold text-base text-white" style={{ fontFamily: DISPLAY }}>Terremoto de magnitud 7,5 con réplica</p>
            <p className="text-xs text-white/60" style={{ fontFamily: MONO }}>Colombia, Venezuela, Caribe Neerlandés, Curazao y Aruba</p>
          </div>
          <ArrowRight className="w-5 h-5 flex-shrink-0 text-white" />
        </div>
      </section>

      {/* Zona neutra entre bloques saturados (naranja alerta / oliva) */}
      <div aria-hidden="true" style={{ background: C.offwhite, height: "var(--space-neutral)" }} data-testid="neutral-zone-2" />

      {/* ═══ EMERGENCIAS ACTIVAS (H2) — Max 2, fondo crema ═══ */}
      {emergencies.length > 0 && (
        <section style={{ background: C.offwhite, paddingBlock: "var(--space-section)" }} data-testid="emergencies-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <span className="inline-block px-2.5 py-1 text-[10px] font-bold tracking-[0.2em] uppercase mb-4"
                style={{ fontFamily: MONO, background: C.orange, color: C.navy }}>
                En curso
              </span>
              <h2 className="text-[clamp(2.2rem,5vw,4rem)] leading-[0.9] tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy, marginBottom: "var(--space-block)" }}>
                Emergencias<br />activas
              </h2>
            </Reveal>
            {emergencies.slice(0, 2).map((emg, idx) => {
              const TypeIcon = EMTYPE_ICONS[emg.type] || AlertTriangle;
              const needsCount = emg.needs_count || 0;
              return (
                <Reveal key={emg.emergency_id} delay={idx * 0.08}>
                  <div style={{ borderTop: idx > 0 ? "1px solid rgba(41,41,77,0.2)" : "none" }}>
                    <div className="cursor-pointer group" onClick={() => navigate("/needs")} data-testid={`emergency-card-${emg.emergency_id}`}
                      style={{ borderLeft: `4px solid ${statusColor(emg.status)}`, paddingLeft: "clamp(20px, 3vw, 40px)", marginBlock: "48px" }}>
                      <div className="grid grid-cols-12 gap-4 md:gap-8 items-start">
                        <div className="col-span-12 md:col-span-8">
                          <div className="flex items-center gap-2 mb-3">
                            <TypeIcon className="w-4.5 h-4.5" style={{ color: C.navy }} strokeWidth={2.5} />
                            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color: C.navy }}>
                              {EMTYPE_LABELS[emg.type] || emg.type}
                            </span>
                          </div>
                          <h3 className="text-2xl md:text-3xl tracking-tight mb-3 group-hover:underline" style={{ fontFamily: DISPLAY, color: C.navy }}>
                            {stripEmojis(emg.title)}
                          </h3>
                          <p className="text-sm md:text-base line-clamp-2 leading-relaxed" style={{ color: C.navy, maxWidth: "65ch" }}>{stripEmojis(emg.description)}</p>
                          {emg.images?.length > 0 && (
                            <div className="flex gap-2 mt-4">
                              {emg.images.slice(0, 3).map((img, i) => (
                                <img key={i}
                                  src={img.startsWith("/") ? `${API.replace(/\/api$/, "")}${img}` : img}
                                  alt={`Imagen ${i + 1}: ${stripEmojis(emg.title)}`}
                                  className="h-20 md:h-24 w-auto object-cover"
                                  style={{ border: `2px solid ${C.navy}`, borderRadius: 2 }}
                                  loading="lazy"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="col-span-12 md:col-span-4 flex md:flex-col items-start md:items-end gap-3 md:gap-2">
                          <span className="flex items-center gap-1 text-[10px] tracking-wider" style={{ fontFamily: MONO, color: "rgba(41,41,77,0.7)" }}>
                            <MapPin className="w-3 h-3" />{emg.location_name || "N/A"}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] tracking-wider" style={{ fontFamily: MONO, color: "rgba(41,41,77,0.7)" }}>
                            <TrendingUp className="w-3 h-3" />{needsCount} {needsCount === 1 ? "necesidad" : "necesidades"}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] tracking-wider" style={{ fontFamily: MONO, color: "rgba(41,41,77,0.7)" }}>
                            <Clock className="w-3 h-3" />{new Date(emg.created_at).toLocaleDateString("es-ES")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
            <div className="flex justify-center" style={{ marginTop: "var(--space-block)" }}>
              <button
                onClick={() => navigate("/needs")}
                className="inline-flex items-center gap-2 px-7 py-3 text-sm transition-all hover:-translate-y-0.5"
                style={{ fontFamily: BODY, fontWeight: 600, background: C.blue, color: C.white, border: `2px solid ${C.navy}`, borderRadius: 2, cursor: "pointer" }}
                data-testid="view-all-emergencies-btn"
              >
                Ver todas las emergencias activas <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ═══ FRANJA VISUAL — foto a sangre con cita editorial ═══ */}
      <section className="relative h-[300px] md:h-[420px] overflow-hidden" data-testid="conecta-photo-band">
        <img
          src="https://customer-assets.emergentagent.com/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/rdp6bkjf_pexels-blanco-studio-2147970603-30374845.jpg"
          alt="Gato descansando a salvo tras ser rescatado"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(41,41,77,0.55)" }}>
          <Reveal>
            <p className="text-center text-[clamp(1.5rem,4vw,3rem)] max-w-3xl px-6 leading-[1.15]" style={{ fontFamily: DISPLAY, color: C.white }}>
              Detrás de cada emergencia<br />
              <span style={{ color: C.orange }}>hay una vida esperando.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ TRES PASOS (H2) — Fondo crema, filas editoriales ═══ */}
      <section style={{ background: C.offwhite, paddingBlock: "var(--space-section)" }} data-testid="how-it-works-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-2" style={{ fontFamily: MONO, color: C.blue }}>
              Como funciona
            </p>
            <h2 className="text-[clamp(2.2rem,5vw,4rem)] leading-[0.9] tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy, marginBottom: "var(--space-block)" }}>
              Tres pasos
            </h2>
          </Reveal>
          {[
            { step: "01", icon: UserPlus, title: "Regístrate", desc: "Crea tu cuenta como voluntario/a o protectora. Gratis, seguro y en menos de un minuto.", accent: C.blue },
            { step: "02", icon: Search, title: "Conecta", desc: "Las protectoras publican sus necesidades en tiempo real. Tú decides cómo ayudar.", accent: C.orange },
            { step: "03", icon: Heart, title: "Actúa", desc: "Ofrece tu ayuda y coordina directamente con la organización que la necesita.", accent: C.orangeRed },
          ].map((item, i) => (
            <div key={item.step} className="ea-step-row" style={{ borderTop: i > 0 ? "1px solid rgba(41,41,77,0.2)" : "none", padding: i === 0 ? "0 0 64px" : "64px 0" }}>
              <Reveal delay={i * 0.08}>
                <div className="grid grid-cols-12 gap-4 md:gap-8 items-start">
                  <div className="col-span-4 md:col-span-3">
                    <span className="ea-step-num text-[6rem] md:text-[10rem] font-bold leading-none block" style={{ fontFamily: DISPLAY, color: item.accent }}>
                      {item.step}
                    </span>
                  </div>
                  <div className="col-span-8 md:col-span-9 pt-4 md:pt-8">
                    <div className="flex items-center gap-3 mb-4">
                      <item.icon className="ea-step-icon w-7 h-7" style={{ color: item.accent }} strokeWidth={2} />
                      <h3 className="text-[clamp(1.8rem,3.5vw,3rem)] tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(41,41,77,0.7)", maxWidth: "65ch" }}>{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ DUAL CTA — banda ambar + bloque crema con filete rosa ═══ */}
      <section data-testid="dual-cta-section">
        <div style={{ background: C.orange }} data-testid="cta-voluntario">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12" style={{ paddingBlock: "var(--space-section)" }}>
            <Reveal>
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ fontFamily: MONO, color: C.navy }}>
                Voluntariado
              </p>
              <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] leading-[0.9] tracking-tight mb-6" style={{ fontFamily: DISPLAY, color: C.navy }}>
                Ofrece tu ayuda<br />donde más se necesita
              </h2>
              <p className="text-base leading-relaxed mb-5" style={{ color: "rgba(41,41,77,0.7)", maxWidth: "65ch" }}>
                La ayuda voluntaria puede ser decisiva, pero funciona mejor cuando está organizada. Al registrarte
                accedes a tu propio espacio: una intranet con tu perfil desde la que puedes ver las necesidades
                que publica cada protectora —transporte, acogida temporal, material o apoyo en el terreno— y
                ofrecer tu ayuda directamente en las que encajen contigo.
              </p>
              <ul className="space-y-2.5 mb-5" style={{ maxWidth: "60ch" }} data-testid="voluntario-benefits-list">
                {[
                  "Un perfil propio con tus datos y tu disponibilidad",
                  "Las necesidades activas de cada protectora, siempre actualizadas",
                  "Alertas en tiempo real cuando surge una emergencia, si decides activarlas",
                  "Un canal directo para ofrecer tu ayuda, sin intermediarios",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm leading-relaxed" style={{ color: "rgba(41,41,77,0.8)" }}>
                    <span aria-hidden="true" className="mt-[7px] w-2 h-2 flex-shrink-0" style={{ background: C.navy }} />
                    {t}
                  </li>
                ))}
              </ul>
              <p className="text-base leading-relaxed mb-8" style={{ color: "rgba(41,41,77,0.7)", maxWidth: "65ch" }}>
                Tú decides cuándo, cómo y cuánto ayudar. Cada ofrecimiento llega ordenado a la protectora,
                para que la solidaridad se convierta en ayuda útil.
              </p>
              <EBtn onClick={() => { if (user?.role === "particular") navigate("/dashboard"); else { localStorage.setItem("ea_pending_auth_role", "particular"); navigate("/auth"); } }}
                bg={C.navy} color={C.white} testId="cta-voluntario-btn">
                Quiero ayudar <ArrowRight className="w-4 h-4" />
              </EBtn>
            </Reveal>
          </div>
        </div>

        {/* Respiro entre ambas secciones (min 120px) */}
        <div aria-hidden="true" style={{ background: C.offwhite, height: "var(--space-neutral)" }} data-testid="neutral-zone-4" />

        <div style={{ background: C.offwhite }} data-testid="cta-protectora">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12" style={{ paddingBottom: "var(--space-section)" }}>
            <Reveal delay={0.1}>
              <div style={{ borderLeft: `4px solid ${C.pink}`, paddingLeft: "clamp(20px, 3vw, 40px)" }}>
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ fontFamily: MONO, color: C.pink }}>
                  Protectoras, refugios, santuarios y colonias felinas
                </p>
                <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] leading-[0.9] tracking-tight mb-6" style={{ fontFamily: DISPLAY, color: C.navy }}>
                  Publica emergencias,<br />coordina la respuesta
                </h2>
                <p className="text-base leading-relaxed mb-5" style={{ color: "rgba(41,41,77,0.7)", maxWidth: "65ch" }}>
                  Si gestionas una protectora, un refugio, un santuario o una colonia felina, aquí tienes un
                  espacio pensado para coordinar la ayuda cuando más se necesita. Al registrar tu entidad accedes
                  a un panel propio desde el que puedes publicar tus necesidades y recibir los ofrecimientos de
                  personas voluntarias de forma ordenada y en tiempo real.
                </p>
                <ul className="space-y-2.5 mb-5" style={{ maxWidth: "60ch" }} data-testid="protectora-benefits-list">
                  {[
                    "Un panel propio para tu entidad",
                    "Publicación de necesidades: transporte, acogida, material o manos",
                    "Ofrecimientos de voluntariado en tiempo real",
                    "Tú decides qué ayuda aceptar y cuándo",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm leading-relaxed" style={{ color: "rgba(41,41,77,0.8)" }}>
                      <span aria-hidden="true" className="mt-[7px] w-2 h-2 flex-shrink-0" style={{ background: C.pink }} />
                      {t}
                    </li>
                  ))}
                </ul>
                <p className="text-base leading-relaxed mb-8" style={{ color: "rgba(41,41,77,0.7)", maxWidth: "65ch" }}>
                  Cada necesidad que publicas queda visible para la red de voluntariado. La coordinación reduce
                  la improvisación: ese es el objetivo.
                </p>
                <EBtn onClick={() => { if (user?.role === "protectora") navigate("/protectora"); else { localStorage.setItem("ea_pending_auth_role", "protectora"); navigate("/auth"); } }}
                  bg={C.navy} color={C.white} testId="cta-protectora-btn">
                  Registrar protectora <ArrowRight className="w-4 h-4" />
                </EBtn>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

    </div>
  );
}
